import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiUserCheck, FiCreditCard, FiAlertCircle, FiXCircle, FiShield, FiInfo, FiCheckCircle } from 'react-icons/fi';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const Section = ({ icon: Icon, title, children }) => (
    <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative group block p-8 rounded-3xl bg-white/[0.02] border border-white/[0.05] hover:border-indigo-500/30 transition-all duration-500 shadow-2xl hover:bg-white/[0.04]"
    >
        <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-500 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-500">
                <Icon size={24} />
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight uppercase tracking-widest leading-none">{title}</h2>
        </div>
        <div className="text-neutral-400 leading-relaxed space-y-4 font-medium">
            {children}
        </div>
    </motion.section>
);

export default function Terms() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-[#050505] flex flex-col selection:bg-indigo-500/30 selection:text-white">
            <Navbar />

            <main className="flex-grow pt-40 pb-32 px-6 relative overflow-hidden">
                {/* Background Glows */}
                <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] -z-10"></div>
                <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-indigo-600/5 rounded-full blur-[120px] -z-10"></div>

                <div className="max-w-5xl mx-auto">
                    {/* Hero Header */}
                    <div className="text-center mb-24 max-w-2xl mx-auto">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-500 text-xs font-black uppercase tracking-[0.2em] mb-8"
                        >
                            <FiShield size={14} /> Legal Framework
                        </motion.div>
                        <h1 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tighter leading-tight">
                            Terms of <span className="text-indigo-600 italic">Service</span>
                        </h1>
                        <p className="text-neutral-500 font-bold tracking-widest uppercase text-xs">
                            Agreement Protocol V.1.2 • Deployment: Jan 2026
                        </p>
                    </div>

                    {/* Content Grid */}
                    <div className="space-y-8">
                        <Section icon={FiUserCheck} title="User Account Protocol">
                            <p>To access proprietary syllabus content, users must maintain an active Academic Profile. By registering, you agree to:</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                                {[
                                    'Submission of validated personal identifiers.',
                                    'Exclusive account ownership (Strictly 1 account/user).',
                                    'Maintaining credential confidentiality.',
                                    'Immediate reporting of security breaches.'
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/[0.05] hover:border-indigo-500/30 transition-all">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 shrink-0 shadow-[0_0_8px_rgba(99,102,241,0.6)]"></div>
                                        <span className="font-bold text-sm text-neutral-300">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </Section>

                        <Section icon={FiCreditCard} title="Enrollment & Financials">
                            <p>Access to the Premium Academic Hub is predicated on month-specific enrollment verification.</p>
                            <ul className="space-y-4 pt-4">
                                {[
                                    { label: 'Verification Window', value: 'Cycles operate on a 24-48 hour window.' },
                                    { label: 'Validation Protocol', value: 'Enrollment is granted upon manual validation of financial slips.' },
                                    { label: 'Refund Architecture', value: 'Zero-Refund policy due to instantaneous digital unlocking.' },
                                    { label: 'Rejection Protocol', value: 'Rejected slips trigger immediate system notification.' }
                                ].map((item, i) => (
                                    <li key={i} className="flex gap-4 group">
                                        <div className="w-5 h-5 rounded-full bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                                            <FiCheckCircle className="text-indigo-500 text-xs" />
                                        </div>
                                        <p className="text-sm">
                                            <span className="text-white font-black uppercase tracking-widest text-[10px] mr-2">{item.label}:</span>
                                            <span className="text-neutral-400">{item.value}</span>
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        </Section>

                        <Section icon={FiAlertCircle} title="Code of Conduct">
                            <p>Users must adhere to the high academic standards of <span className="font-bold text-white">Maths with Manoj</span>.</p>
                            <div className="bg-red-500/5 border border-red-500/20 p-8 rounded-[2rem] text-red-500 mt-6 shadow-inner relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-10"><FiXCircle size={60} /></div>
                                <h4 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                                    Critical Violations
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3 text-sm font-bold opacity-90">
                                    <p className="flex items-center gap-2"><span>•</span> Redistribution of Content</p>
                                    <p className="flex items-center gap-2"><span>•</span> Video Interception</p>
                                    <p className="flex items-center gap-2"><span>•</span> Multi-Device Access</p>
                                    <p className="flex items-center gap-2"><span>•</span> System Interference</p>
                                </div>
                            </div>
                        </Section>

                        <Section icon={FiShield} title="Intellectual Property">
                            <p>
                                All mathematical concepts, video streams, delivery materials, and source code are the proprietary assets of <span className="font-bold text-white">Manoj Sampath Academics</span>.
                                Dynamic watermarking encodes user-specific data into every frame to deter leakage and trace intellectual theft.
                            </p>
                        </Section>

                        <Section icon={FiInfo} title="Protocol Amendments">
                            <p>
                                We reserve the administrative right to update this framework periodically. Users will be notified via the Academic Dashboard (HUD) of significant synchronizations.
                            </p>
                        </Section>

                        {/* Final Agreement Card */}
                        <div className="pt-20 border-t border-white/[0.05] text-center">
                            <p className="text-[10px] text-neutral-600 font-black uppercase tracking-[0.3em] max-w-2xl mx-auto leading-relaxed">
                                By engaging with the Maths with Manoj Platform, you signify your absolute compliance with the technical and legal protocols outlined in this agreement.
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}
